import Country from '../../../assets/enums/config/Country'
import {
  hasSection,
  hasSubSection
} from '@/assets/settings/SectionByDossierType'

import { mapGetters } from 'vuex'

import eventType from '@/assets/enums/events/EventType'
import DossierType from '@/assets/enums/dossier/DossierType'
import { ItemCategoryGroup } from '@/assets/enums/index.js'
import {
  base_order_funeral,
  base_order_remain
} from '@/assets/settings/EventsOrder'

export default {
  data() {
    return {}
  },
  computed: {
    ...mapGetters({
      editionMode: 'dossier/getEditionMode',
      dossier: 'dossier/getDossier',
      auth: 'auth/getAuth',
      settings: 'metadata/getInitialMetadata',
      defaultCountry: 'metadata/getDefaultCountry',
      statusSummary: 'dossier/getStatusSummary',
      itemCategoriesGroupByParentAndGroup:
        'metadata/getMetadataItemCategoriesGroupByParentAndGroup',
      fh_settings: 'metadata/getFuneralHomeSettings'
    }),
    firstMenu() {
      let funeralOrder = this.settings?.settings?.events_order?.funeral || []
      let remainOrder = this.settings?.settings?.events_order?.remain || []
      let infosDossierOrder = this.settings?.settings?.infos_dossier_order || []

      // Supprime l’event ASR de la navigation si de pas de CRE
      const hasCREEvent = this.dossier?.events?.some((e) => e.type === 'CRE')
      if (!hasCREEvent) {
        funeralOrder = funeralOrder.filter((type) => type !== 'ASR')
      }

      // Supprime l’event CRAR de la navigation si pas de CRE
      const hasCRAREvent = this.dossier?.events?.some((e) => e.type === 'CRE')
      if (!hasCRAREvent) {
        funeralOrder = funeralOrder.filter((type) => type !== 'CRAR')
      }

      const orderByBaseList = (items, baseOrder) => {
        const indexOf = (key) => {
          const idx = baseOrder.indexOf(key)
          return idx === -1 ? Number.MAX_SAFE_INTEGER : idx
        }
        return [...items].sort((a, b) => indexOf(a.key) - indexOf(b.key))
      }

      const reorderSubItems = (
        subItems,
        orderList,
        appendMissing = false,
        baseOrder = []
      ) => {
        if (!Array.isArray(subItems) || !orderList.length) return subItems

        let ordered = orderList
          .map((type) => {
            const item = subItems.find((i) => i.key === type)
            if (!item) return null

            const labelKey = item.anchor?.replace('#', '') + '_enabled'
            const isEnabled = this.settings?.settings?.[labelKey] ?? true

            return isEnabled ? item : null
          })
          .filter(Boolean)

        //ici, je force l'insertion de profession et children après deceased dans la navigation
        const civilInformationItem = subItems.find(
          (i) => i.key === 'civil_information'
        )
        const profItem = subItems.find((i) => i.key === 'profession')
        const childrenItem = subItems.find((i) => i.key === 'children')

        ordered = ordered.filter(
          (i) =>
            i.key !== 'profession' &&
            i.key !== 'children' &&
            i.key !== 'civil_information'
        )

        const deceasedIndex = ordered.findIndex((i) => i.key === 'deceased')
        if (deceasedIndex !== -1) {
          const toInsert = [
            civilInformationItem,
            profItem,
            childrenItem
          ].filter(Boolean)
          ordered.splice(deceasedIndex + 1, 0, ...toInsert)
        }

        // Append enabled sub-items that are entirely absent from the persisted
        // order list, so newly enabled events show up in the menu without
        // resetting the order. Items already in the order list keep their
        // existing handling (purely additive, no behaviour change otherwise).
        // Appended items follow the canonical base order as a fallback position.
        if (appendMissing) {
          const missingItems = subItems.filter(
            (item) => item.key && !orderList.includes(item.key)
          )
          ordered.push(...orderByBaseList(missingItems, baseOrder))
        }

        return ordered
      }

      const funeralSubItemsRaw = [
        {
          label: this.$t('enums.event.type.DECL'),
          anchor: '#declaration',
          key: 'DECL'
        },
        {
          label: this.$t('enums.event.type.CRE'),
          anchor: '#cremation',
          key: 'CRE'
        },
        {
          label: this.$t('enums.event.type.CER'),
          anchor: '#ceremony',
          key: 'CER'
        },
        { label: this.$t('enums.event.type.WAK'), anchor: '#wake', key: 'WAK' },
        {
          label:
            this.dossier?.type === DossierType.MARBRERIE
              ? this.$t('attributes.events')
              : this.dossier?.events?.some((e) => e.type === 'CRE')
                ? this.$t('attributes.burial_type')
                : this.$t('enums.event.type.BUR'),
          anchor: '#burial',
          key: 'BUR'
        },
        {
          label: this.$t('enums.event.type.BDR'),
          anchor: '#body_raising',
          key: 'BDR'
        },
        {
          label: this.$t('enums.event.type.CRAR'),
          anchor: '#crematorium_arrival',
          key: 'CRAR'
        },
        {
          label: this.$t('enums.event.type.ASR'),
          anchor: '#ashes_recovery',
          key: 'ASR'
        },
        {
          label: this.$t('enums.event.type.REU'),
          anchor: '#reunion',
          key: 'REU'
        },
        {
          label: this.$t('enums.event.type.VIS'),
          anchor: '#visit',
          key: 'VIS'
        },
        {
          label: this.$t('enums.event.type.ROO'),
          anchor: '#room_occupancy',
          key: 'ROO'
        },
        {
          label: this.$t('enums.event.type.REC'),
          anchor: '#reception',
          key: 'REC'
        },
        {
          label: this.$t('enums.event.type.EXH'),
          anchor: '#exhumation',
          key: 'EXH'
        },
        {
          label: this.$t('enums.event.type.MAR'),
          anchor: '#marbrerie',
          key: 'MAR'
        },
        {
          label: this.$t('enums.event.type.APPOI'),
          anchor: '#appointment',
          key: 'APPOI'
        }
      ]

      const infosSubItemsRaw = [
        {
          label: this.$t('global.informations'),
          anchor: '#informations',
          key: 'informations'
        },
        {
          label:
            this.dossier?.type === DossierType.MARBRERIE
              ? this.$t('attributes.customer')
              : [DossierType.CONTRAT].includes(this.dossier?.type)
                ? this.$t('global.beneficiary')
                : this.$t('global.deceased'),
          anchor: '#deceased',
          key: 'deceased'
        },
        {
          label: this.$t('global.civil_information'),
          anchor: '#civil_information',
          key: 'civil_information'
        },
        {
          label: this.$t('global.profession'),
          anchor: '#profession',
          key: 'profession'
        },
        {
          label: this.$t('global.spouses'),
          anchor: '#marital_status',
          key: 'marital_status'
        },
        {
          label: this.$t('global.parents'),
          anchor: '#parent',
          key: 'parent'
        },
        {
          label: this.$t('global.children'),
          anchor: '#children',
          key: 'children'
        },
        {
          label: this.$t('global.death'),
          anchor: '#death',
          key: 'death'
        },
        {
          label: this.$t('global.others'),
          anchor: '#other',
          key: 'other'
        },
        {
          label: this.$t('attributes.cemetery'),
          anchor: '#cemetery',
          key: 'cemetery'
        },
        {
          label: this.$t('global.partners'),
          anchor: '#partners',
          key: 'partners'
        }
      ]

      const remainSubItemsRaw = [
        {
          label: this.$t(`enums.event.type.${eventType.TRANSFERT}`),
          anchor: '#transfert',
          key: 'TRA'
        },
        {
          label: this.$t(`enums.event.type.${eventType.THANATO}`),
          anchor: '#thanato',
          key: 'THAN'
        },
        {
          label: this.$t(`attributes.pacemaker`),
          anchor: '#pacemaker'
        },
        {
          label: this.$t(`enums.event.type.${eventType.COFFIN_PLACING}`),
          anchor: '#coffin_placing',
          key: 'COP'
        },
        {
          label: this.$t(`enums.event.type.${eventType.COFFIN_CLOSING}`),
          anchor: '#coffin_closing',
          key: 'COC'
        },
        {
          label: this.$t(`enums.event.type.${eventType.SEALING_PROCESS}`),
          anchor: '#sealing_process',
          key: 'SPR'
        },
        {
          label: this.$t(`attributes.other_infos`),
          anchor: '#other_infos'
        }
      ]

      // Like reorderSubItems but handles pacemaker (after thanato) and other_infos (always last)
      // since they have no EventType code and are not part of the events_order array.
      const reorderRemainSubItems = (subItems, orderList) => {
        if (!Array.isArray(subItems) || !orderList.length) return subItems

        let ordered = orderList
          .map((type) => {
            const item = subItems.find((i) => i.key === type)
            if (!item) return null
            const labelKey = item.anchor?.replace('#', '') + '_enabled'
            const isEnabled = this.settings?.settings?.[labelKey] ?? true
            return isEnabled ? item : null
          })
          .filter(Boolean)

        const pacemakerItem = subItems.find((i) => i.anchor === '#pacemaker')
        if (pacemakerItem) {
          const thanatoIdx = ordered.findIndex((i) => i.key === 'THAN')
          if (thanatoIdx !== -1) {
            ordered.splice(thanatoIdx + 1, 0, pacemakerItem)
          } else {
            ordered.push(pacemakerItem)
          }
        }

        // Append enabled event sub-items entirely absent from the persisted order
        // list, so newly enabled events show up without resetting the order.
        // Appended items follow the canonical base order as a fallback position.
        const missingItems = subItems.filter(
          (item) => item.key && !orderList.includes(item.key)
        )
        ordered.push(...orderByBaseList(missingItems, base_order_remain))

        const otherInfosItem = subItems.find((i) => i.anchor === '#other_infos')
        if (otherInfosItem) {
          ordered.push(otherInfosItem)
        }

        return ordered
      }

      const filteredFuneralItems = this.filterSubItemsByConfig(
        funeralSubItemsRaw,
        'funerals'
      )
      const filteredInfosItems = this.filterSubItemsByConfig(
        infosSubItemsRaw,
        'informations'
      )
      const filteredRemainItems = this.filterSubItemsByConfig(
        remainSubItemsRaw,
        'remains'
      )

      let subItemFunerals = reorderSubItems(
        filteredFuneralItems,
        funeralOrder,
        true,
        base_order_funeral
      )

      // ASR/CRAR are only relevant when a cremation event exists. The append step
      // could re-add them, so enforce the rule on the final list as well.
      if (!hasCREEvent) {
        subItemFunerals = subItemFunerals.filter(
          (item) => item.key !== 'ASR' && item.key !== 'CRAR'
        )
      }
      const subItemInfosDossier = reorderSubItems(
        filteredInfosItems,
        infosDossierOrder
      )
      const subItemRemains = reorderRemainSubItems(
        filteredRemainItems,
        remainOrder
      )

      return [
        {
          id: 'summary',
          label: this.$t('global.summary'),
          routeName: 'dossier_form_edit_route',
          icon: ['fas', 'house-blank'],
          gate: this.cIsEditView,
          subItems: null,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'informations',
          label: this.$t('global.informations'),
          routeName: this.cInformationRoute,
          icon: ['fas', 'user'],
          gate:
            hasSection(this.dossier?.type, 'informations') &&
            (this.cIsEditView || this.cIsCreateView),
          subItems: subItemInfosDossier,
          formType: ['normal', 'light'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'contacts',
          label: this.$t('global.contacts'),
          routeName: this.cContactRoute,
          icon: ['fas', 'users'],
          gate: this.cIsEditView && hasSection(this.dossier?.type, 'contacts'),
          subItems: null,
          formType: ['normal', 'light'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'remains',
          label: this.$t('global.remains'),
          routeName: this.cRemainsRoute,
          icon: ['fas', 'user-tag'],
          gate: this.cIsEditView && hasSection(this.dossier?.type, 'remains'),
          subItems: subItemRemains,
          formType: ['normal', 'light'],
          countries: [Country.FRANCE, Country.MARTINIQUE]
        },
        {
          id: 'funerals',
          label:
            this.dossier?.type === DossierType.MARBRERIE
              ? this.$t('attributes.events')
              : this.$t('global.funerals'),
          routeName: this.cFuneralRoute,
          icon: ['fas', 'user-tie'],
          gate: this.cIsEditView && hasSection(this.dossier?.type, 'funerals'),
          subItems: subItemFunerals,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'coffin_and_products',
          label: this.$t('global.coffin_and_products'),
          routeName: this.cCoffinAndProductRoute,
          icon: ['fas', 'coffin'],
          gate:
            this.cIsEditView &&
            hasSection(this.dossier?.type, 'coffin_and_products') &&
            this.itemCategoriesGroupByParentAndGroup.hasOwnProperty(
              ItemCategoryGroup.FUNERAL
            ),
          subItems: null,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'remains',
          label: this.$t('global.remains'),
          routeName: this.cRemainsRoute,
          icon: ['fas', 'user-tag'],
          gate: this.cIsEditView && hasSection(this.dossier?.type, 'remains'),
          subItems: subItemRemains,
          formType: ['normal', 'light'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        // {
        //   label: this.$t('global.insurance'),
        //   routeName: this.editionMode
        //     ? 'dossier_form_insurance_route_edit'
        //     : 'dossier_form_insurance_route',
        //   icon: ['fas', 'piggy-bank'],
        //   gate: this.cIsEditView,
        //   subItems: null,
        //   formType: ['normal'],
        //   countries: [
        //     Country.BELGIUM,
        //     Country.SWITZERLAND,
        //     Country.FRANCE,
        //     Country.NETHERLANDS,
        //     Country.SPAIN,
        //     Country.ITALY,
        //     Country.PORTUGAL,
        //     Country.GERMANY,
        //     Country.LUXEMBOURG
        //   ]
        // },
        {
          id: 'particularities',
          label: this.$t('global.particularities'),
          routeName: this.cParticularitiesRoute,
          icon: ['fas', 'comment-question'],
          gate:
            this.cIsEditView &&
            hasSection(this.dossier?.type, 'particularities'),
          subItems: null,
          formType: ['normal', 'light'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'flowers',
          label: this.$t('global.flowers'),
          routeName: this.cFlowersRoute,
          icon: ['fas', 'flower'],
          gate:
            this.cIsEditView &&
            hasSection(this.dossier?.type, 'flowers') &&
            this.itemCategoriesGroupByParentAndGroup.hasOwnProperty(
              ItemCategoryGroup.FLOWERS
            ),
          subItems: null,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'prints',
          label: this.$t('global.prints'),
          routeName: this.cPrintsRoute,
          icon: ['fas', 'print'],
          gate:
            this.cIsEditView &&
            hasSection(this.dossier?.type, 'printings') &&
            this.itemCategoriesGroupByParentAndGroup.hasOwnProperty(
              ItemCategoryGroup.PRINTS
            ),
          subItems: null,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'other_products',
          label: this.$t('global.other_products'),
          routeName: this.cOtherProductsRoute,
          icon: ['fas', 'tag'],
          gate:
            this.cIsEditView &&
            hasSection(this.dossier?.type, 'other_products') &&
            this.itemCategoriesGroupByParentAndGroup.hasOwnProperty(
              ItemCategoryGroup.OTHERS
            ),
          subItems: null,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'external_services',
          label: this.$t('global.external_services'),
          routeName: this.cExternalServicesRoute,
          icon: ['fas', 'share-nodes'],
          gate:
            this.cIsEditView &&
            hasSection(this.dossier?.type, 'external_services') &&
            this.itemCategoriesGroupByParentAndGroup.hasOwnProperty(
              ItemCategoryGroup.EXTERNAL_SERVICES
            ),
          subItems: null,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'other_events',
          label: this.$t('global.other_events'),
          routeName: this.cOtherEventsRoute,
          icon: ['far', 'calendar'],
          gate:
            this.cIsEditView && hasSection(this.dossier?.type, 'other_events'),
          subItems: null,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'mourning_reception',
          label: this.$t('global.mourning_reception'),
          routeName: 'dossier_form_mourning_reception_route',
          icon: ['fas', 'mug'],
          gate: false,
          subItems: null,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'financing',
          label: this.$t('global.financing'),
          routeName: 'dossier_form_financing_route',
          icon: ['fas', 'piggy-bank'],
          gate: hasSection(this.dossier?.type, 'financing'),
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          id: 'obituary',
          label: this.$t('global.obituary'),
          routeName: 'dossier_form_obituary_route',
          icon: ['fas', 'newspaper'],
          gate: false,
          subItems: null,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        }
      ]
    },
    secondMenu() {
      return [
        {
          label: this.$t('global.documents'),
          routeName: 'dossier_documents_router_route',
          icon: ['fas', 'file-lines'],
          gate: this.cIsEditView,
          hiddenOnMobile: true,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          label: this.$t('navigation.messaging_title'),
          routeName: 'dossier_mail_box_page',
          icon: ['fas', 'mailbox'],
          gate: this.cIsEditView,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ],
          badges: [
            {
              value: this.statusSummary?.unreaded_incoming_email ?? null,
              // tint: 'metallic-orange',
              tint: null,
              icon: ['fal', 'envelope']
            }
          ]
        },
        {
          label: this.$t('global.attachments'),
          routeName: this.dossier?.light_form
            ? 'dossier_light_form_attachments_route'
            : 'dossier_form_attachments_route',
          icon: ['fas', 'paperclip'],
          gate: this.cIsEditView && this.$can('see_attachment'),
          formType: ['normal', 'light'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          label: this.$t('global.gallery'),
          routeName: 'dossier_gallery_list_route',
          icon: ['fas', 'image-portrait'],
          gate: this.cIsEditView && this.$can('manage_dossier'),
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          label: this.$t('global.condolence'),
          routeName: 'dossier_form_condolence_route',
          icon: ['fas', 'feather'],
          gate: hasSection(this.dossier?.type, 'condolences'),
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          label: this.$t('global.invoicing'),
          routeName: 'dossier_form_invoicing_route',
          icon: ['fas', 'euro-sign'],
          gate: this.cIsEditView && this.$can('see_invoicing'),
          hiddenOnMobile: true,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          label: this.$t('global.supplier_orders'),
          routeName: 'dossier_supplier_order_route',
          icon: ['fas', 'truck-clock'],
          gate: this.cIsEditView,
          hiddenOnMobile: true,
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ]
        },
        {
          label: this.$t('global.tasks'),
          routeName: 'dossier_form_tasks_route',
          icon: ['fas', 'list-check'],
          gate: this.cIsEditView && this.$can('see_tasks'),
          formType: ['normal'],
          countries: [
            Country.BELGIUM,
            Country.SWITZERLAND,
            Country.FRANCE,
            Country.MARTINIQUE,
            Country.NETHERLANDS,
            Country.SPAIN,
            Country.ITALY,
            Country.PORTUGAL,
            Country.GERMANY,
            Country.LUXEMBOURG
          ],
          badges: [
            {
              value: this.statusSummary?.tasks_to_do ?? null,
              // tint: 'metallic-orange',
              tint: null
            }
          ]
        }
        // {
        //   label: this.$t('global.condolences'),
        //   routeName: 'dossier_form_condolence_route',
        //   icon: ['fas', 'feather'],
        //   gate: hasSection(this.dossier?.type, 'condolences'),
        //   formType: ['normal'],
        //   countries: [
        //     Country.BELGIUM,
        //     Country.SWITZERLAND,
        //     Country.FRANCE,
        //     Country.MARTINIQUE,
        //     Country.NETHERLANDS,
        //     Country.SPAIN,
        //     Country.ITALY,
        //     Country.PORTUGAL,
        //     Country.GERMANY,
        //     Country.LUXEMBOURG
        //   ]
        // }
      ]
    },
    countryForMenuOrder() {
      const fhSettings = this.fh_settings?.[this.dossier?.funeral_home_id]
      const fhMenuOrder = fhSettings?.fh_menu_order
      if (fhMenuOrder === 'SPECIFIC')
        return fhSettings?.fh_menu_order_country || this.defaultCountry
      if (fhMenuOrder === 'USER' && this.$i18n.locale)
        return this.$i18n.locale.slice(3) || this.defaultCountry
      return this.defaultCountry
    },
    firstMenuEntries() {
      return this.firstMenu
        .filter((entry) => entry.gate)
        .filter(
          (entry) =>
            !this.$route?.meta?.form ||
            entry.formType.includes(this.$route.meta.form)
        )
        .filter((entry) => entry.countries.includes(this.countryForMenuOrder))
    },
    secondMenuEntries() {
      return this.secondMenu
        .filter((entry) => entry.gate)
        .filter(
          (entry) =>
            !this.$route?.meta?.form ||
            entry.formType.includes(this.$route.meta.form)
        )
        .filter((entry) => entry.gate)
        .filter((entry) => entry.countries.includes(this.countryForMenuOrder))
    },
    firstMenuHasPreviousEntry() {
      const index = this.firstMenuEntries.findIndex(
        (entry) => entry.routeName === this.$route.name
      )
      return index && index > 0
    },
    firstMenuHasNextEntry() {
      const index = this.firstMenuEntries.findIndex(
        (entry) => entry.routeName === this.$route.name
      )
      return index && index + 1 < this.firstMenuEntries.length
    },
    firstMenuPreviousEntry() {
      const index = this.firstMenuEntries.findIndex(
        (entry) => entry.routeName === this.$route.name
      )
      return this.firstMenuEntries[index - 1]
    },
    firstMenuNextEntry() {
      const index = this.firstMenuEntries.findIndex(
        (entry) => entry.routeName === this.$route.name
      )
      return this.firstMenuEntries[index + 1]
    },
    cIsEditView() {
      return this.$route.meta.type === 'edit'
    },
    cIsDisplayView() {
      return this.$store.getters['auth/getDisplayMode'] === 'display'
    },
    cInformationRoute() {
      if (this.cIsCreateView) {
        return 'dossier_form_new_route'
      }

      const isNormalForm = this.$route.meta.form === 'normal'

      if (!isNormalForm) {
        return 'dossier_light_form_information_route'
      }

      if (this.editionMode) {
        return 'dossier_form_information_route_edit'
      }

      if (this.cIsDisplayView) {
        return 'dossier_form_information_route_display'
      }

      return 'dossier_form_information_route'
    },
    cContactRoute() {
      if (this.cIsCreateView) {
        return 'dossier_form_contact_route_new'
      }

      const isNormalForm = this.$route.meta.form === 'normal'

      if (!isNormalForm) {
        return 'dossier_light_form_contact_route'
      }

      if (this.editionMode) {
        return 'dossier_form_contact_route_edit'
      }

      if (this.cIsDisplayView) {
        return 'dossier_form_contact_route_display'
      }

      return 'dossier_form_contact_route'
    },
    cRemainsRoute() {
      if (this.cIsCreateView) {
        return 'dossier_form_remains_route_new'
      }

      const isNormalForm = this.$route.meta.form === 'normal'

      if (!isNormalForm) {
        return 'dossier_light_form_remains_route'
      }

      if (this.editionMode) {
        return 'dossier_form_remains_route_edit'
      }

      if (this.cIsDisplayView) {
        return 'dossier_form_remains_route_display'
      }

      return 'dossier_form_remains_route'
    },
    cFuneralRoute() {
      if (this.cIsCreateView) {
        return 'dossier_form_funeral_route_new'
      }

      const isNormalForm = this.$route.meta.form === 'normal'

      if (!isNormalForm) {
        return 'dossier_light_form_funeral_route'
      }

      if (this.editionMode) {
        return 'dossier_form_funeral_route_edit'
      }

      if (this.cIsDisplayView) {
        return 'dossier_form_funeral_route_display'
      }
      return 'dossier_form_funeral_route'
    },
    cCoffinAndProductRoute() {
      if (this.cIsCreateView) {
        return 'dossier_form_coffin_and_product_route_new'
      }

      if (this.editionMode) {
        return 'dossier_form_coffin_and_product_route_edit'
      }

      if (this.cIsDisplayView) {
        return 'dossier_form_coffin_and_product_route_display'
      }

      return 'dossier_form_coffin_and_product_route'
    },
    cParticularitiesRoute() {
      if (this.cIsCreateView) {
        return 'dossier_form_particularities_route_new'
      }

      const isNormalForm = this.$route.meta.form === 'normal'

      if (!isNormalForm) {
        return 'dossier_light_form_particularity_route'
      }

      if (this.editionMode) {
        return 'dossier_form_particularities_route_edit'
      }

      if (this.cIsDisplayView) {
        return 'dossier_form_particularities_route_display'
      }

      return 'dossier_form_particularities_route'
    },
    cOtherProductsRoute() {
      if (this.cIsCreateView) {
        return 'dossier_form_other_products_route_new'
      }

      if (this.editionMode) {
        return 'dossier_form_other_products_route_edit'
      }

      if (this.cIsDisplayView) {
        return 'dossier_form_other_products_route_display'
      }

      return 'dossier_form_other_products_route'
    },
    cFlowersRoute() {
      if (this.cIsCreateView) {
        return 'dossier_form_flowers_route_new'
      }

      if (this.editionMode) {
        return 'dossier_form_flowers_route_edit'
      }

      if (this.cIsDisplayView) {
        return 'dossier_form_flowers_route_display'
      }

      return 'dossier_form_flowers_route'
    },
    cPrintsRoute() {
      if (this.cIsCreateView) {
        return 'dossier_form_prints_route_new'
      }

      if (this.editionMode) {
        return 'dossier_form_prints_route_edit'
      }

      if (this.cIsDisplayView) {
        return 'dossier_form_prints_route_display'
      }

      return 'dossier_form_prints_route'
    },
    cExternalServicesRoute() {
      if (this.cIsCreateView) {
        return 'dossier_form_external_services_route_new'
      }

      if (this.editionMode) {
        return 'dossier_form_external_services_route_edit'
      }

      if (this.cIsDisplayView) {
        return 'dossier_form_external_services_route_display'
      }

      return 'dossier_form_external_services_route'
    },
    cOtherEventsRoute() {
      if (this.cIsCreateView) {
        return 'dossier_form_other_events_route_new'
      }

      if (this.editionMode) {
        return 'dossier_form_other_events_route_edit'
      }

      if (this.cIsDisplayView) {
        return 'dossier_form_other_events_route_display'
      }

      return 'dossier_form_other_events_route'
    }
  },
  methods: {
    hasSection,
    hasSubSection,
    filterSubItemsByConfig(subItems, section) {
      if (!this.dossier?.type) return []

      return subItems.filter((item) => {
        const subSection = item.anchor?.replace('#', '')
        const isValidSubSection = hasSubSection(
          this.dossier?.type,
          section,
          subSection
        )
        // Si on est dans 'funerals' ou 'remains', on se rassure que l'evenement a été activé dans les settings
        if (['funerals', 'remains'].includes(section)) {
          const isSettingEnabled =
            this.$h.getSettingValue(
              this.dossier.funeral_home_id,
              `${subSection}_enabled`,
              this.settings
            ) || ['other_infos'].includes(subSection)
          return isValidSubSection && isSettingEnabled
        }

        return isValidSubSection
      })
    }
  }
}
